/**
 * Utility functions for formatting Solidity names and types for display in the UI.
 */

import { startCase } from 'lodash';

type SolidityNameType = 'Contract' | 'Parameter' | 'Method' | 'Event';

/**
 * Convert a Solidity parameter/method/contract name to a human-readable format.
 *
 * Examples:
 * - "myAddress" -> "My Address"
 * - "user_id" -> "User ID"
 * - "tokenAmount" -> "Token Amount"
 *
 * @param name - The Solidity name to format
 * @param solidityNameType - The type of Solidity name (for unnamed parameters)
 * @returns The formatted name, or a fallback for unnamed parameters
 */
export function getReadableSolidityName(
  name: string,
  solidityNameType: SolidityNameType
): string {
  // Case: Unnamed parameter
  if (name === '') {
    return `Unnamed ${solidityNameType}`;
  }

  // Turn into Start Case (handles camelCase, snake_case, etc.)
  const startCaseName = startCase(name);

  // Replace "Id" with "ID" for better readability
  const readableName = startCaseName.replace(/\bId\b/g, 'ID');

  return readableName;
}
